import { usePrevious } from "@react-hookz/web";
import { useMultipleSelection, UseMultipleSelectionProps } from "downshift";
import { useI18n } from "../../../i18n/i18n";

interface useMultipleSelectionCustomOptions<Option> {
    isInteractive: boolean;
    itemToString: UseMultipleSelectionProps<Option>["itemToString"];
    onChange: (newOptions: Option[]) => void;
    rollupSelectionAfter: number;
    selectedItems: Option[];
}

export const useMultipleSelectionCustom = <Option>({
    isInteractive,
    itemToString,
    rollupSelectionAfter,
    onChange,
    selectedItems = [],
}: useMultipleSelectionCustomOptions<Option>) => {
    const { i18n } = useI18n();

    const previousSelectedCount = usePrevious(selectedItems.length);

    const hookReturn = useMultipleSelection({
        getA11yRemovalMessage: (opts) => {
            const { removedSelectedItem, itemToString } = opts;

            // If our new count is 0, and the previous count was more than 1 item, the only way to
            // go from 2+ items to 0 is to click select all.
            // There's an edge case here if there is only 1 option in the multiselect, but we should
            // probably not show "select all" in that case...
            const selectAllClicked =
                opts.resultCount === 0 && previousSelectedCount !== undefined && previousSelectedCount > 1;

            if (selectAllClicked) {
                return i18n._(/*i18n*/ "All items have been removed.");
            } else {
                return i18n._(
                    /*i18n*/ {
                        id: "{itemAsString} has been removed.",
                        values: {
                            itemAsString: itemToString(removedSelectedItem),
                        },
                    }
                );
            }
        },
        itemToString: itemToString,
        selectedItems: selectedItems,
        onSelectedItemsChange: (change) => onChange(change.selectedItems || []),
        stateReducer: (state, { changes, type }) => {
            // Cancel the following events if the dropdown is non-interactive.
            switch (type) {
                case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownDelete:
                case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownBackspace:
                case useMultipleSelection.stateChangeTypes.DropdownKeyDownBackspace:
                    if (!isInteractive) {
                        return state;
                    }
            }

            const isSelectionCombined =
                rollupSelectionAfter !== undefined && rollupSelectionAfter < state.selectedItems.length;

            switch (type) {
                case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownDelete:
                case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownBackspace:
                case useMultipleSelection.stateChangeTypes.FunctionSetSelectedItems:
                    // Only allow updating selected state when MS is interactive (not disabled & not readonly).
                    return isInteractive ? changes : state;
                case useMultipleSelection.stateChangeTypes.FunctionRemoveSelectedItem:
                    return {
                        ...changes,
                        // If there's nothing selected, and backspace is pressed in an empty input,
                        // normally downshift will delete the last chip and focus the one before
                        // the deleted one. This is kinda weird; we'll just keep the input focused.
                        activeIndex: state.activeIndex < 0 ? -1 : changes.activeIndex,
                    };
                case useMultipleSelection.stateChangeTypes.DropdownKeyDownNavigationPrevious: {
                    const inputFocused = state.activeIndex < 0;

                    if (inputFocused && isSelectionCombined) {
                        return {
                            ...changes,
                            // If the "go to previous chip" key is pressed (ArrowLeft) and the input is
                            // focused, we need to check to see if we're currently rolling any items up.
                            // If we are, we need to tell downshift to focus the rolled-up chip which
                            // has an activeIndex much less than selectedItems.length -1.
                            activeIndex: rollupSelectionAfter,
                        };
                    } else {
                        return changes;
                    }
                }
                case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownNavigationNext: {
                    const isCombinedChipFocused = state.activeIndex === rollupSelectionAfter;
                    return {
                        ...changes,
                        // If we're focused on the combined chip an ArrowRight should
                        // focus the input.
                        activeIndex: isSelectionCombined && isCombinedChipFocused ? -1 : changes.activeIndex,
                    };
                }
            }
            return changes;
        },
    });

    const selectedItemSet = new Set(selectedItems);

    return {
        isItemSelected: (option: Option) => selectedItemSet.has(option),
        ...hookReturn,
    };
};
